import {
  Button,
  MenuItem,
  ClickAwayListener,
  Paper,
  ButtonProps,
  Popper,
  ButtonGroup,
  Grow,
  MenuList,
  Box,
  BoxProps,
} from '@mui/material';
import {
  ALL_RECORD_TYPE_IDS,
  ALL_RECORD_TYPES,
  FlagshipModelOptions,
} from 'constants/recordTypes';
import { useDocumentTemplates } from 'hooks/useDocumentTemplates';
import useSnackbar from 'hooks/useSnackbar';
import { useGeneratePurchaseOrderPdfMutation } from 'store/slices/apiV1/procurement';
import { DocumentTemplate, StockDocument, StockDocType } from 'types/inventory';
import _ from 'lodash';
import { useRef, useState, MouseEvent, useEffect } from 'react';
import { handleErr } from 'utils/functions';
import { IconChevronDown, IconFileExport } from '@tabler/icons-react';
import WithSkeleton from 'ui-component/extended/WithSkeleton';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import {
  ReportTemplateSchema,
  ReportTemplateSchemaLite,
} from 'types/clientV2/reporting';
import { useGetReportTemplatesQuery } from 'store/slices/clientV2/reporting';
import { ALL, DISABLE_PAGE_LIMIT, QueryFilterOperators } from 'types/api';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import useGenerateReports, {
  ReportType,
  GenerateReportReturnBuffer,
} from 'hooks/useGenerateReport';
import { useCreateStockDocsV2 } from 'hooks/useCreateStockDocsV2';
import { useCreateStockDocumentRelatedObjectMutation } from 'store/slices/clientV2/stockDocuments';
import { StockDocumentRelatedObjectPOSTSchema } from 'types/clientV2/stockDocuments';
import { OrgLite } from 'types/org';

type DocumentTemplateOption = DocumentTemplate & { id: string };
type GenerateDocumentOption =
  | { kind: 'document'; template: DocumentTemplateOption }
  | { kind: 'report'; template: ReportTemplateSchemaLite };

interface GenerateDocumentButtonProps<ReportDataType = never> {
  recordType: ALL_RECORD_TYPE_IDS;
  defaultDocumentTemplateId?: DocumentTemplate['id'] | null;
  size?: ButtonProps['size'];
  additionalGenerateProps?: object;
  setSelectedDocumentId?: (id: string) => void;
  boxSx?: BoxProps['sx'];
  isLoading?: boolean;
  generateReportData?: ReportDataType;
  recordId?: string;
  recordOrgId?: string | null;
}

const GenerateDocumentButton = <ReportDataType = never,>({
  recordType,
  defaultDocumentTemplateId,
  size,
  additionalGenerateProps,
  setSelectedDocumentId,
  boxSx,
  isLoading,
  generateReportData,
  recordId,
  recordOrgId,
}: GenerateDocumentButtonProps<ReportDataType>) => {
  const tempKitDocumentationZip = useFeature(
    FeatureFlags.tempKitDocumentationZip
  ).on;
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const recordTypeDefinition = ALL_RECORD_TYPES[recordType];
  const generatePdfMutation =
    recordTypeDefinition?.generatePdfMutation ||
    useGeneratePurchaseOrderPdfMutation;

  const [generatePdf, { isLoading: isLoadingGeneratePdf }] =
    generatePdfMutation();

  const { generateReport, loading: isLoadingGenerateReport } =
    useGenerateReports(!!generateReportData);
  const { createStockDocuments, isLoading: isLoadingCreateStockDoc } =
    useCreateStockDocsV2();
  const [
    createStockDocumentRelatedObject,
    { isLoading: isLoadingCreateStockDocumentRelatedObject },
  ] = useCreateStockDocumentRelatedObjectMutation();

  const {
    allDocumentTemplates,
    defaultDocumentTemplates,
    groupedDocumentTemplates,
    fetching: isFetchingDocumentTemplates,
  } = useDocumentTemplates();

  const { data: templatesData } = useServerSideQuery<
    ReportTemplateSchema,
    ReportTemplateSchemaLite
  >(
    useGetReportTemplatesQuery,
    recordTypeDefinition.reportingTemplateName
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          schema: [
            ALL,
            {
              codeSnippetQuery: [],
              codeSnippetPreProcessor: [],
              codeSnippetPostProcessor: [],
            },
          ],
          filters: [
            {
              field: 'associatedDatagrid',
              operator: QueryFilterOperators['='],
              value: recordTypeDefinition.reportingTemplateName,
            },
          ],
        }
      : skipToken
  );

  const documentTemplates =
    recordType === ALL_RECORD_TYPE_IDS.SHIPMENT && tempKitDocumentationZip
      ? _.chain(groupedDocumentTemplates)
          .get(recordTypeDefinition.generatePdfDocumentType || '', [])
          .map((documentTemplate) => [
            documentTemplate,
            {
              ...documentTemplate,
              id: `${documentTemplate.id}-zip`,
              name: `${documentTemplate.name} (Traceability Package Zip)`,
              externalName: `${documentTemplate.externalName} (Traceability Package Zip)`,
            },
          ])
          .flatten()
          .value()
      : _.get(
          groupedDocumentTemplates,
          recordTypeDefinition.generatePdfDocumentType || '',
          []
        );

  const defaultDocumentTemplate = defaultDocumentTemplateId
    ? _.find(allDocumentTemplates, { id: defaultDocumentTemplateId }) ||
      _.get(
        defaultDocumentTemplates,
        recordTypeDefinition.generatePdfDocumentType || '',
        null
      )
    : _.get(
        defaultDocumentTemplates,
        recordTypeDefinition.generatePdfDocumentType || '',
        null
      );

  const documentOptions: GenerateDocumentOption[] = documentTemplates.map(
    (t) => ({ kind: 'document', template: t as DocumentTemplateOption })
  );
  const reportTemplates = templatesData?.data ?? [];
  const reportOptions: GenerateDocumentOption[] = reportTemplates.map((t) => ({
    kind: 'report',
    template: t,
  }));
  const combinedOptions: GenerateDocumentOption[] = [
    ...documentOptions,
    ...reportOptions,
  ];

  const getDefaultSelectedIndex = () => {
    if (defaultDocumentTemplate?.id) {
      const idx = documentTemplates.findIndex(
        (dt) => dt.id === defaultDocumentTemplate?.id
      );
      if (idx >= 0) return idx;
    }
    return 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(getDefaultSelectedIndex());

  useEffect(() => {
    if (isFetchingDocumentTemplates) return;
    setSelectedIndex(getDefaultSelectedIndex());
  }, [isFetchingDocumentTemplates]);

  const selectedOption = combinedOptions[selectedIndex];
  const selectedDocumentTemplateId =
    selectedOption?.kind === 'document' ? selectedOption.template.id : null;

  const options = combinedOptions.map((opt) =>
    opt.kind === 'document'
      ? opt.template.externalName || opt.template.name
      : opt.template.name
  );

  const onGenerateDocument = async () => {
    if (!selectedOption) return;
    if (selectedOption.kind === 'report') {
      if (!generateReportData || !recordId) {
        dispatchErrorSnackbar('Missing data or record to generate report');
        return;
      }
      const gridName = recordTypeDefinition.reportingTemplateName ?? 'rfq';
      try {
        const result = (await generateReport({
          type: ReportType.REPORT_TEMPLATE,
          template: selectedOption.template,
          data: generateReportData,
          gridName,
          returnBuffer: true,
        })) as GenerateReportReturnBuffer | void;
        if (!result) return;
        const file = new File([result.buffer], result.filename, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const createResults = await createStockDocuments([
          {
            file,
            documentType:
              recordTypeDefinition.generatePdfDocumentType ??
              StockDocType.REPORT,
            filename: result.filename,
            createdFromObjectId: recordId,
            createdFromObjectType:
              recordTypeDefinition.flagshipModel ??
              FlagshipModelOptions.STOCK_LOT,
          },
        ]);
        const firstResult = createResults[0];
        if (firstResult.status !== 'fulfilled' || !firstResult.value?.id) {
          dispatchErrorSnackbar('Failed to create stock document');
          return;
        }
        const joinPayload: StockDocumentRelatedObjectPOSTSchema = {
          stockDocument: firstResult.value.id,
          relatedObjectId: recordId,
          relatedObjectType: recordTypeDefinition.flagshipModel ?? '',
          org: (recordOrgId ?? undefined) as OrgLite['id'],
        };
        await createStockDocumentRelatedObject([joinPayload]).unwrap();
        setSelectedDocumentId?.(firstResult.value.id);
        dispatchSuccessSnackbar('Document Generated');
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
      return;
    }
    if (!selectedDocumentTemplateId) return;
    try {
      let document: StockDocument | null = null;
      if (_.endsWith(selectedDocumentTemplateId, '-zip')) {
        document = (await generatePdf({
          ...(additionalGenerateProps || {}),
          payload: {
            documentTemplate: _.replace(selectedDocumentTemplateId, '-zip', ''),
            generateZip: true,
          },
        }).unwrap()) as StockDocument;
      } else {
        document = (await generatePdf({
          ...(additionalGenerateProps || {}),
          payload: {
            documentTemplate: selectedDocumentTemplateId,
          },
        }).unwrap()) as StockDocument;
      }
      setSelectedDocumentId?.(document?.id);
      dispatchSuccessSnackbar('Document Generated');
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        dispatchErrorSnackbar(errMessage);
      });
    }
  };

  const handleMenuItemClick = (
    _event: MouseEvent<HTMLLIElement>,
    index: number
  ) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <Box sx={boxSx}>
      <WithSkeleton isLoading={isLoading}>
        <ButtonGroup
          variant="contained"
          ref={anchorRef}
          aria-label="Button group with a nested menu"
        >
          <Button
            loading={
              isLoadingGeneratePdf ||
              isLoadingGenerateReport ||
              isLoadingCreateStockDoc ||
              isLoadingCreateStockDocumentRelatedObject
            }
            variant="contained"
            onClick={onGenerateDocument}
            size={size}
            startIcon={<IconFileExport />}
          >
            {options[selectedIndex] ?? ''}
          </Button>
          <Button
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
            size={size}
            sx={{
              px: '5px',
              minWidth: '20px',
              '&.MuiButtonGroup-grouped': { px: '5px', minWidth: '20px' },
            }}
          >
            <IconChevronDown />
          </Button>
        </ButtonGroup>
      </WithSkeleton>
      <Popper
        sx={{ zIndex: 10000 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {combinedOptions.map((opt, index) => (
                    <MenuItem
                      key={`${opt.kind}-${opt.template.id}`}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {options[index]}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default GenerateDocumentButton;
